/**
 *
 * The shadowDom / Intersection Observer version of Paul's concept:
 * https://github.com/paulirish/lite-youtube-embed
 *
 * A lightweight YouTube / Vimeo embed. Still should feel the same to the user, just
 * MUCH faster to initialize and paint.
 *
 * Thx to these as the inspiration
 *   https://storage.googleapis.com/amp-vs-non-amp/youtube-lazy.html
 *   https://autoplay-youtube-player.glitch.me/
 *   https://github.com/justinribeiro/lite-youtube/
 *   https://github.com/slightlyoff/lite-vimeo/
 *
 *
 */

import EventHandler from '../../utils/dom/event-handler';
import SelectorEngine from '../../utils/dom/selector-engine';
import dataLayer from '../../utils/data-layer';
import Manipulator from '../../utils/dom/manipulator';
import { VIMEO_ACCESS_TOKEN } from '../../utils/constants';

export class LiteVideoEmbed extends HTMLElement {
	constructor() {
		super();
		this.iframeLoaded = false;
		this.thrdAccept;
	}

	static get observedAttributes() {
		return ['videotype', 'videoid', 'videosrc'];
	}

	connectedCallback() {
		this.setupDom();
		this.initEvents();
		this.cookieHandler();
		this.addEventListener('pointerover', LiteVideoEmbed.warmConnections, {
			once: true
		});
		this.addEventListener('click', () => this.addIframe());

		// If videoid is already set when element connects, initialize the component
		// This ensures setupComponent() is called after shadow DOM is ready
		if ((this.getAttribute('videoid') || this.getAttribute('videosrc')) && this.shadowRoot && this.domRefImg) {
			this.setupComponent();
		}
	}

	get fallbackThumbnail() {
		return this.getAttribute('fallback-thumbnail') || '';
	}

	set fallbackThumbnail(thumbnail) {
		this.setAttribute('fallback-thumbnail', thumbnail);
	}

	get videoType() {
		return encodeURIComponent(this.getAttribute('videotype') || '');
	}

	set videoType(type) {
		this.setAttribute('videotype', type);
	}

	get videoId() {
		return encodeURIComponent(this.getAttribute('videoid') || '');
	}

	set videoId(id) {
		this.setAttribute('videoid', id);
	}

	get videoSrc() {
		return this.getAttribute('videosrc') || this.getAttribute('videoid') || '';
	}

	set videoSrc(source) {
		this.setAttribute('videosrc', source);
	}

	get videoTitle() {
		return this.getAttribute('videotitle') || 'Video';
	}

	set videoTitle(title) {
		this.setAttribute('videotitle', title);
	}

	get videoPlay() {
		return this.getAttribute('videoplay') || 'Play';
	}

	set videoPlay(name) {
		this.setAttribute('videoplay', name);
	}

	get videoStartAt() {
		return Number(this.getAttribute('videoStartAt') || '0');
	}

	set videoStartAt(time) {
		this.setAttribute('videoStartAt', String(time));
	}

	get autoLoad() {
		return this.hasAttribute('autoload');
	}

	set autoLoad(value) {
		if (value) {
			this.setAttribute('autoload', '');
		} else {
			this.removeAttribute('autoload');
		}
	}

	get autoPlay() {
		return this.hasAttribute('autoplay');
	}

	set autoPlay(value) {
		if (value) {
			this.setAttribute('autoplay', 'autoplay');
		} else {
			this.removeAttribute('autoplay');
		}
	}

	get params() {
		return `start=${this.videoStartAt}&${this.getAttribute('params')}`;
	}

	/**
	 * Define our shadowDOM for the component
	 */
	setupDom() {
		const shadowDom = this.attachShadow({ mode: 'open' });
		shadowDom.innerHTML = `
		<style>
			:host {
				contain: content;
				display: block;
				position: relative;
				width: 100%;
				padding-bottom: calc(100% / (16 / 9));
			}
			:host-context(.swiper-slide:not(.swiper-slide-active)) iframe,
			:host-context(.swiper-slide:not(.swiper-slide-active)) video,
			:host-context(.swiper-slide:not(.swiper-slide-active)) .lty-playbtn {
				display: block;
			}
			#frame, #fallbackPlaceholder, iframe, video {
				position: absolute;
				width: 100%;
				height: 100%;
			}
			video {
				top: 0;
				left: 0;
				z-index: 2;
				background: #000;
			}
			#frame {
				cursor: pointer;
			}
			#fallbackPlaceholder {
				object-fit: cover;
			}
			#frame::before {
				content: '';
				display: block;
				position: absolute;
				top: 0;
				background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAADGCAYAAAAT+OqFAAAAdklEQVQoz42QQQ7AIAgEF/T/D+kbq/RWAlnQyyazA4aoAB4FsBSA/bFjuF1EOL7VbrIrBuusmrt4ZZORfb6ehbWdnRHEIiITaEUKa5EJqUakRSaEYBJSCY2dEstQY7AuxahwXFrvZmWl2rh4JZ07z9dLtesfNj5q0FU3A5ObbwAAAABJRU5ErkJggg==);
				background-position: top;
				background-repeat: repeat-x;
				height: 60px;
				padding-bottom: 50px;
				width: 100%;
				transition: all 0.2s cubic-bezier(0, 0, 0.2, 1);
				z-index: 1;
			}
			/* play button */
			.lty-playbtn,
			.lty-playbtn:before {
				position: absolute;
				top: 50%;
			}

			.lty-playbtn {
				z-index: 3;
			}

      .lty-playbtn svg {
        width: 100%;
        height: 100%;
      }

			@media (min-width: 768px) {
				.lty-playbtn,
				.lty-playbtn:before {
					top: 29px;
					right: 24px;
					left: auto;
					transform: none;
          background: none;
          border: none;
          height: 44px;
          width: 44px;
          padding: 0;
				}
			}

			@media (min-width: 1200px) {
				.lty-playbtn,
				.lty-playbtn:before {
					top: 50px;
          height: 64px;
          width: 64px;
				}
			}

			/* Post-click styles */
			.lyt-activated {
				cursor: unset;
			}
			#frame.lyt-activated::before {
				display: none !important;
			}
			:host([videotype='youtube']) .lyt-activated .lty-playbtn,
			:host([videotype='vimeo']) .lyt-activated .lty-playbtn,
			:host(:not([showcontrols='false'])) .lyt-activated .lty-playbtn {
				display: none !important;
			}
			#frame.lyt-activated picture {
				display: none !important;
			}
		</style>
		<div id="frame">
		  <picture>
			<source id="webpPlaceholder" type="image/webp">
			<source id="jpegPlaceholder" type="image/jpeg">
			<img id="fallbackPlaceholder" referrerpolicy="origin">
		  </picture>
		  <button class="lty-playbtn">
		  	<svg class="fe-icon" fill="currentColor">
          <use xlink:href="/static/images/icons/lta-icons.svg#tf-play-pause"></use>
        </svg>
		  </button>
		</div>
	  `;
		this.domRefFrame = this.shadowRoot.querySelector('#frame');
		this.domRefImg = {
			fallback: this.shadowRoot.querySelector('#fallbackPlaceholder'),
			webp: this.shadowRoot.querySelector('#webpPlaceholder'),
			jpeg: this.shadowRoot.querySelector('#jpegPlaceholder')
		};
		this.domRefPlayButton = this.shadowRoot.querySelector('.lty-playbtn');
		this.videoWrapper = this.shadowRoot.host.closest('figure');
		this.isPrivate = Manipulator.getDataAttribute(this.videoWrapper, 'is-private');
		this.privateAuthKey = Manipulator.getDataAttribute(this.videoWrapper, 'private-auth-key');
	}

	initEvents() {
		this.addEventListener('lta-litevideo-event', (event) => {
			const { command } = event.detail;

			this.onVideoCommand(command);
		});

		EventHandler.on(this.domRefPlayButton, 'click', (event) => {
			const liteVideoElement = event.target.getRootNode().host.closest('figure');
			const videoNameAttribute = Manipulator.getDataAttribute(liteVideoElement, 'video-name');
			const videoDescAttribute = Manipulator.getDataAttribute(liteVideoElement, 'video-description');
			const videoUrlAttribute = Manipulator.getDataAttribute(liteVideoElement, 'video-url');
			const videoDateAttribute = Manipulator.getDataAttribute(liteVideoElement, 'video-date');
			const videoEmbedAttribute = {
				videoName: videoNameAttribute,
				videoDescription: videoDescAttribute,
				videoURL: videoUrlAttribute,
				VideoDate: videoDateAttribute
			};
			dataLayer.push({ event: 'videoView', ...videoEmbedAttribute });
		});
	}

	/**
	 * Parse our attributes and fire up some placeholders
	 */
	setupComponent() {
		// Ensure shadow DOM is ready before trying to access DOM references
		if (!this.shadowRoot || !this.domRefImg || !this.domRefImg.fallback || !this.domRefPlayButton) {
			// Shadow DOM not ready yet, skip initialization
			// It will be called again from connectedCallback when ready
			return;
		}

		this.initImagePlaceholder();
		this.domRefPlayButton.setAttribute('aria-label', `${this.videoPlay}: ${this.videoTitle}`);
		this.setAttribute('title', `${this.videoPlay}: ${this.videoTitle}`);
		if (this.autoLoad) {
			this.initIntersectionObserver();
		}

		if (this.videoType === 'mp4' && !this.iframeLoaded) {
			this.addIframe();
		}
	}

	/**
	 * Lifecycle method that we use to listen for attribute changes to period
	 *
	 * @param {*} name
	 * @param {*} oldVal
	 * @param oldValue
	 * @param {*} newVal
	 * @param newValue
	 */
	attributeChangedCallback(name, oldValue, newValue) {
		switch (name) {
			case 'videoid':
			case 'videosrc': {
				if (oldValue !== newValue) {
					// if we have a previous iframe, remove it and the activated class
					if (this.domRefFrame && this.shadowRoot && this.domRefFrame.classList.contains('lyt-activated')) {
						this.domRefFrame.classList.remove('lyt-activated');
						this.classList.remove('lyt-activated');
						this.shadowRoot.querySelector('iframe')?.remove();
						this.shadowRoot.querySelector('video')?.remove();
						this.iframeLoaded = false;
					}

					this.setupComponent();
				}
				break;
			}
			// eslint-disable-next-line no-fallthrough
			default:
				break;
		}
	}

	onVideoCommand(command) {
		const iframe = this.shadowRoot.querySelector('iframe');
		const video = this.shadowRoot.querySelector('video');

		if (video) {
			switch (command) {
				case 'pause':
					video.pause();
					break;
				case 'play':
					video.play().catch(() => false);
					break;
				default:
					break;
			}

			return;
		}

		if (iframe) {
			switch (command) {
				case 'pause':
					if (this.videoType === 'youtube') {
						iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
					} else if (this.videoType === 'vimeo') {
						iframe.contentWindow.postMessage('{"method":"pause","value":"true"}', '*');
					}
					break;
				default:
					break;
			}
		}
	}

	cookieHandler() {
		import('js-cookie')
			.then(({ default: Cookies }) => {
				this.thrdAccept = Cookies.get('cookieprefs') ? JSON.parse(Cookies.get('cookieprefs')).thrd : false;

				return 1;
			})
			.catch((error) => {
				console.log(error);
			});
	}
	/**
	 * Inject the iframe into the component body
	 */
	addIframe() {
		// Addition for dealing with the case where the third party cookies are not accepted

		if (!this.iframeLoaded) {
			let iframeHTML;

			switch (this.videoType) {
				case 'youtube': {
					iframeHTML =
						`
<iframe frameborder="0"
  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen
  src="https://www.youtube` +
						(this.thrdAccept ? `` : `-nocookie`) +
						`.com/embed/${this.videoId}?autoplay=1&enablejsapi=1&${this.params}"></iframe>`;

					break;
				}
				case 'vimeo': {
					const apValue = (this.autoLoad && this.autoPlay) || !this.autoLoad ? 'autoplay=1' : '';
					const sourceUrl = new URL(
						`/video/${this.videoId}?${apValue}&#t=${this.videoStartAt}&api=1`,
						'https://player.vimeo.com/'
					);

					// TODO: construct src value w/ URL constructor
					iframeHTML =
						`
<iframe frameborder="0"
  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen ` +
						(this.thrdAccept ? `` : `dnt="true"`) +
						`
  src="${sourceUrl}"></iframe>`;

					break;
				}
				case 'mp4': {
					const autoPlayAttribute = this.autoPlay ? 'autoplay' : '';
					const posterAttribute = this.fallbackThumbnail ? `poster="${this.fallbackThumbnail}"` : '';
					iframeHTML = `
<video controls playsinline preload="metadata" src="${this.videoSrc}" ${posterAttribute} ${autoPlayAttribute}></video>`;

					break;
				}
				// No default
			}

			if (!iframeHTML) {
				return;
			}

			this.domRefFrame.insertAdjacentHTML('beforeend', iframeHTML);
			this.domRefFrame.classList.add('lyt-activated');
			this.classList.add('lyt-activated');
			this.iframeLoaded = true;

			if (this.videoType === 'mp4') {
				const videoElement = this.shadowRoot.querySelector('video');
				if (videoElement) {
					videoElement.addEventListener('error', () => {
						if (window.console && typeof window.console.warn === 'function') {
							window.console.warn('[lite-video-tf] MP4 failed to load or play', {
								src: this.videoSrc,
								errorCode: videoElement.error ? videoElement.error.code : undefined
							});
						}
					});

					videoElement.play().catch(() => false);
				}
			}
		}
	}

	/**
	 * Setup the placeholder image for the component
	 */
	async initImagePlaceholder() {
		// we don't know which image type to preload, so warm the connection
		let posterUrlWebp;
		let posterUrlJpeg;

		if (this.videoType === 'youtube') {
			LiteVideoEmbed.addPrefetch('preconnect', 'https://i.ytimg.com/');
			posterUrlWebp = `https://i.ytimg.com/vi_webp/${this.videoId}/maxresdefault.webp`; // hqdefault
			posterUrlJpeg = `https://i.ytimg.com/vi/${this.videoId}/hqdefault.jpg`;
		} else if (this.videoType === 'vimeo') {
			LiteVideoEmbed.addPrefetch('preconnect', 'https://i.vimeocdn.com/');
			const myHeaders = new Headers();
			if (this.isPrivate) {
				myHeaders.append('Authorization', `Bearer ${this.privateAuthKey || VIMEO_ACCESS_TOKEN}`);
			}
			const requestOptions = {
				method: 'GET',
				headers: myHeaders
			};

			await fetch(`https://api.vimeo.com/videos/${this.videoId}/pictures`, requestOptions)
				.then((response) => response.json())
				// eslint-disable-next-line promise/always-return
				.then((result) => {
					// eslint-disable-next-line promise/always-return
					if (result?.data?.length > 0) {
						const tnLarge = result?.data[0]?.sizes[0]?.link;
						const imgId = tnLarge.slice(tnLarge.lastIndexOf('/') + 1).split('_')[0];
						posterUrlWebp = `https://i.vimeocdn.com/video/${imgId}.webp?mw=1100&mh=619&q=70`;
						posterUrlJpeg = `https://i.vimeocdn.com/video/${imgId}.jpg?mw=1100&mh=619&q=70`;
					}
				})
				.catch((error) => console.error(error));
		}

		if (this.videoType === 'mp4') {
			if (this.fallbackThumbnail) {
				this.domRefImg.fallback.src = this.fallbackThumbnail;
			} else {
				this.domRefImg.webp.removeAttribute('srcset');
				this.domRefImg.jpeg.removeAttribute('srcset');
				this.domRefImg.fallback.removeAttribute('src');
			}
		} else if (!this.fallbackThumbnail) {
			this.domRefImg.webp.srcset = posterUrlWebp;
			this.domRefImg.jpeg.srcset = posterUrlJpeg;
			this.domRefImg.fallback.src = posterUrlJpeg;
		} else {
			this.domRefImg.fallback.src = this.fallbackThumbnail;
		}
		this.domRefImg.fallback.setAttribute('aria-label', `${this.videoPlay}: ${this.videoTitle}`);
		this.domRefImg.fallback.setAttribute('alt', `${this.videoPlay}: ${this.videoTitle}`);
	}

	/**
	 * Setup the Intersection Observer to load the iframe when scrolled into view
	 */
	initIntersectionObserver() {
		if ('IntersectionObserver' in window && 'IntersectionObserverEntry' in window) {
			const options = {
				root: null,
				rootMargin: '0px',
				threshold: 0
			};
			const observer = new IntersectionObserver((entries, observer) => {
				for (const entry of entries) {
					if (entry.isIntersecting && !this.iframeLoaded) {
						LiteVideoEmbed.warmConnections();
						this.addIframe();
						observer.unobserve(this);
					}
				}
			}, options);
			observer.observe(this);
		}
	}

	/**
	 * Add a <link rel={preload | preconnect} ...> to the head
	 *
	 * @param {*} kind
	 * @param {*} url
	 * @param {*} as
	 */
	static addPrefetch(kind, url, as) {
		const linkElement = document.createElement('link');
		linkElement.rel = kind;
		linkElement.href = url;
		if (as) {
			linkElement.as = as;
		}
		linkElement.crossOrigin = 'true';
		document.head.append(linkElement);
	}

	/**
	 * Begin preconnecting to warm up the iframe load Since the embed's netwok
	 * requests load within its iframe, preload/prefetch'ing them outside the
	 * iframe will only cause double-downloads. So, the best we can do is warm up
	 * a few connections to origins that are in the critical path.
	 *
	 * Maybe `<link rel=preload as=document>` would work, but it's unsupported:
	 * http://crbug.com/593267 But TBH, I don't think it'll happen soon with Site
	 * Isolation and split caches adding serious complexity.
	 */
	static warmConnections() {
		if (LiteVideoEmbed.preconnected) return;

		if (this.videoType === 'youtube') {
			// Host that YT uses to serve JS needed by player, per amp-youtube
			LiteVideoEmbed.addPrefetch('preconnect', 'https://s.ytimg.com');

			// The iframe document and most of its subresources come right off
			// youtube.com
			LiteVideoEmbed.addPrefetch('preconnect', 'https://www.youtube.com');

			// The botguard script is fetched off from google.com
			LiteVideoEmbed.addPrefetch('preconnect', 'https://www.google.com');

			// TODO: Not certain if these ad related domains are in the critical path.
			// Could verify with domain-specific throttling.
			LiteVideoEmbed.addPrefetch('preconnect', 'https://googleads.g.doubleclick.net');
			LiteVideoEmbed.addPrefetch('preconnect', 'https://static.doubleclick.net');
		} else if (this.videoType === 'vimeo') {
			// Host that Vimeo uses to serve JS needed by player
			LiteVideoEmbed.addPrefetch('preconnect', 'https://f.vimeocdn.com');
			// The iframe document comes from player.vimeo.com
			LiteVideoEmbed.addPrefetch('preconnect', 'https://player.vimeo.com');
			// Image for placeholder comes from i.vimeocdn.com
			LiteVideoEmbed.addPrefetch('preconnect', 'https://i.vimeocdn.com');
		}

		LiteVideoEmbed.preconnected = true;
	}
}

LiteVideoEmbed.preconnected = false;

// Register custom element
customElements.define('lite-video-tf', LiteVideoEmbed);

const setupNativeMp4VideoEmbed = () => {
	for (const element of document.querySelectorAll('.js-lta-video-embed-mp4')) {
		if (element.dataset.mp4VideoEmbedInitialized === 'true') {
			continue;
		}

		element.dataset.mp4VideoEmbedInitialized = 'true';
		const video = element.querySelector('video');
		const toggleButton = element.querySelector('.js-lta-video-embed-mp4-toggle');

		if (!video || !toggleButton) {
			continue;
		}

		const updateToggleState = () => {
			const isPlaying = !video.paused && !video.ended;
			toggleButton.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
			toggleButton.classList.toggle('is-playing', isPlaying);
		};

		toggleButton.addEventListener('click', (event) => {
			event.preventDefault();
			event.stopPropagation();
			if (video.paused || video.ended) {
				video.play().catch(() => false);
				return;
			}

			video.pause();
		});

		video.addEventListener('play', updateToggleState);
		video.addEventListener('pause', updateToggleState);
		video.addEventListener('ended', updateToggleState);

		updateToggleState();
	}
};

const toggleNativeMp4Playback = (button) => {
	const container = button.closest('.js-lta-video-embed-mp4');
	if (!container) {
		return;
	}

	const video = container.querySelector('video');
	if (!video) {
		return;
	}

	if (video.paused || video.ended) {
		video.play().catch(() => false);
		return;
	}

	video.pause();
};

const pauseVideoHeroEmbedsBelowMd = () => {
	if (window.matchMedia('(min-width: 768px)').matches) {
		return;
	}

	for (const video of document.querySelectorAll('.video-hero-block__video-shape .js-lta-video-embed-mp4 video')) {
		if (!video.paused) {
			video.pause();
		}
	}

	for (const liteVideo of document.querySelectorAll('.video-hero-block__video-shape lite-video-tf')) {
		liteVideo.dispatchEvent(
			new CustomEvent('lta-litevideo-event', {
				detail: { command: 'pause' }
			})
		);

		if (typeof liteVideo.onVideoCommand === 'function') {
			liteVideo.onVideoCommand('pause');
		}
	}
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => {
		setupNativeMp4VideoEmbed();
		pauseVideoHeroEmbedsBelowMd();
	});
} else {
	setupNativeMp4VideoEmbed();
	pauseVideoHeroEmbedsBelowMd();
}

window.addEventListener('load', () => {
	setupNativeMp4VideoEmbed();
	pauseVideoHeroEmbedsBelowMd();
});

if (document.documentElement.dataset.videoHeroResizePauseBound !== 'true') {
	document.documentElement.dataset.videoHeroResizePauseBound = 'true';
	window.addEventListener('resize', pauseVideoHeroEmbedsBelowMd);
}

if (document.documentElement.dataset.nativeMp4VideoEmbedClickBound !== 'true') {
	document.documentElement.dataset.nativeMp4VideoEmbedClickBound = 'true';
	document.addEventListener('click', (event) => {
		if (event.defaultPrevented) {
			return;
		}

		const toggleButton = event.target.closest('.js-lta-video-embed-mp4-toggle');
		if (!toggleButton) {
			return;
		}

		event.preventDefault();
		toggleNativeMp4Playback(toggleButton);
	});
}
